import { startCase } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  HighlightOff as HighlightOffIcon,
  OfflinePinOutlined as OfflinePinOutlinedIcon
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import {
  State,
  StateTransitionCondition,
  WorkflowParams
} from '@sierralabs/workflow';

import { FetchError } from '../../../../common/api/api';
import ConfirmDialog from '../../../../common/dialogs/ConfirmDialog';
import WrappedTableCell from '../../../../common/WrappedTableCell';
import EntitySchemaSelector from '../../../entity/components/EntitySchemaSelector';
import EntitySchemaRelationSelector from '../../../entity/components/EntitySchemaRelationSelector';
import { useEntityContext } from '../../../entity/Entity.context';
import { useCheckDependencies } from '../../../tasks/Task.hooks';
import { CheckDependenciesResult } from '../../../tasks/Task.interface';
import { TaskContext } from '../../../tasks/Task.machine';
import {
  displayEntityProperties,
  getEntityProperties
} from '../../../entity/Entity.hooks';
import { EntitySchema } from '../../../entity/types';
import WorkflowSelector from '../../components/WorkflowSelector';
import { useWorkflowContext } from '../../Workflow.context';
import { useWorkflowStatuses } from '../../Workflow.hooks';
import {
  TransitionConditionConfiguratorProps,
  TransitionConditionDisplayProps,
  TransitionConditionPlugin
} from '../TransitionConditionPlugin';

function displayStatuses(
  statuses: string[] = [],
  wrap = true,
  defaultValue = 'Any'
) {
  const allStatuses = statuses.join(', ');
  const status = wrap && statuses.length > 1 ? `(${allStatuses})` : allStatuses;
  return status || defaultValue;
}

export interface EntityDependency {
  name?: string;
  workflowId: number;
  propertyName: string; // relation.propertyName
  // join relations; i.e. propertyName->relations[0]->...relations[n]
  relations: string[];
  statuses: string[]; // workflow.status
  // allow no items from this dependency
  optional?: boolean;
}

export interface CheckDependenciesParams extends WorkflowParams {
  entityName: string; // entitySchema.name
  dependencies?: EntityDependency[];
}

const CheckDependenciesConfigurator: React.FC<
  TransitionConditionConfiguratorProps
> = props => {
  const { taskType, condition, onChange } = props;
  const [open, setOpen] = useState(false);
  const [updateIndex, setUpdateIndex] = useState(-1);
  const [removeIndex, setRemoveIndex] = useState(-1);
  if (!condition.params) {
    condition.params = {};
  }
  const params = condition.params as CheckDependenciesParams;
  const { schemas } = useEntityContext();
  // entity schema of current Workflow
  const schema = taskType
    ? schemas.find(schema => schema.name === taskType)
    : undefined;

  const getDisplayName = (
    dependency: EntityDependency | undefined,
    includeStatus = false
  ) => {
    if (!dependency) {
      return '?';
    }
    const properties = getEntityProperties(
      [dependency.propertyName].concat(dependency.relations || []),
      schema,
      schemas
    );
    const property = properties
      .map(property => {
        return property.relation?.displayName || property.propertyName || '?';
      })
      .join(' -> ');
    const entity = schema?.displayName || '?';
    const displayName = `${entity} -> ${property}`;
    const status = displayStatuses(dependency.statuses);
    return includeStatus ? `${displayName} -> ${status}` : displayName;
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Dependency</TableCell>
              <TableCell>Required State/s</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!params.dependencies?.length && (
              <TableRow>
                <TableCell colSpan={4}>No dependencies.</TableCell>
              </TableRow>
            )}
            {params.dependencies?.map((dependency, index) => {
              const relations = [
                dependency.propertyName,
                ...(dependency.relations || [])
              ];
              const propertyName = relations[relations.length - 1];
              return (
                <TableRow key={`${dependency.name}-${propertyName}-${index}`}>
                  <WrappedTableCell>
                    {dependency.name || startCase(propertyName)}
                  </WrappedTableCell>
                  <WrappedTableCell>
                    {getDisplayName(dependency)}
                    {dependency.optional && (
                      <>
                        {' '}
                        <Typography
                          variant='body2'
                          color='textSecondary'
                          style={{ display: 'inline-block' }}
                        >
                          (optional)
                        </Typography>
                      </>
                    )}
                  </WrappedTableCell>
                  <WrappedTableCell>
                    {displayStatuses(dependency.statuses, false)}
                  </WrappedTableCell>
                  <TableCell padding='checkbox'>
                    <IconButton
                      size='small'
                      onClick={() => setUpdateIndex(index)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => setRemoveIndex(index)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Button
          variant='outlined'
          disabled={!schema}
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add Dependency
        </Button>
      </Box>

      <Collapse in={!schema}>
        <Box mt={2}>
          <Alert severity='error'>No schema found for this Workflow.</Alert>
        </Box>
      </Collapse>

      {schema && (
        <CheckDependenciesEntityDialog
          schema={schema}
          open={open || updateIndex > -1}
          dependency={params.dependencies?.[updateIndex]}
          title={updateIndex > -1 ? 'Update Dependency' : 'Add Dependency'}
          onCancel={() => {
            setOpen(false);
            setUpdateIndex(-1);
          }}
          onSubmit={dependency => {
            params.entityName = schema.name;
            if (!params.dependencies) {
              params.dependencies = [];
            }
            if (updateIndex > -1) {
              params.dependencies.splice(updateIndex, 1, dependency);
            } else {
              params.dependencies.push(dependency);
            }
            setOpen(false);
            setUpdateIndex(-1);
            onChange?.({ ...condition });
          }}
        />
      )}

      <ConfirmDialog
        confirmLabel='Remove'
        open={removeIndex > -1}
        title='Remove Dependency'
        message={`Are you sure you want to remove ${getDisplayName(
          params.dependencies?.[removeIndex],
          true
        )} dependency?`}
        onCancel={() => setRemoveIndex(-1)}
        onConfirm={() => {
          if (!params.dependencies) {
            params.dependencies = [];
          }
          if (removeIndex > -1) {
            params.dependencies.splice(removeIndex, 1);
          }
          setRemoveIndex(-1);
          onChange?.({ ...condition });
        }}
      />
    </>
  );
};

const StyledBox = styled(Box)`
  .grid-field {
    flex: 1;
  }
  .grid-action {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
  }
`;

export interface CheckDependenciesEntityDialogProps {
  open: boolean;
  title: string;
  schema: EntitySchema;
  dependency?: EntityDependency;
  onCancel?: () => void;
  onSubmit?: (dependency: EntityDependency) => void;
}

export const CheckDependenciesEntityDialog: React.FC<
  CheckDependenciesEntityDialogProps
> = props => {
  const {
    open,
    title,
    schema,
    dependency: dependencyProp,
    onCancel,
    onSubmit
  } = props;
  const [dependency, setDependency] = useState<Partial<EntityDependency>>(
    dependencyProp || { relations: [], statuses: [] }
  );
  const { schemas } = useEntityContext();
  const { getWorkflow } = useWorkflowContext();
  const workflow = useMemo(() => {
    return dependency.workflowId
      ? getWorkflow(dependency.workflowId)
      : undefined;
  }, [dependency.workflowId, getWorkflow]);
  const statuses = useWorkflowStatuses(workflow);
  const isValidStatus = useMemo(() => {
    return (
      Array.isArray(dependency.statuses) &&
      // allow optional statuses
      // dependency.statuses.length > 0 &&
      dependency.statuses.every(status => statuses.includes(status)) &&
      (!dependency.relations ||
        dependency.relations.every(relation => !!relation))
    );
  }, [dependency, statuses]);

  const properties = useMemo(() => {
    return getEntityProperties(
      [dependency.propertyName].concat(dependency.relations || []),
      schema,
      schemas
    );
  }, [dependency, schema, schemas]);

  // get last property.relation
  const relation = useMemo(() => {
    return properties[properties.length - 1]?.relation;
  }, [properties]);

  const entityDependency: EntityDependency | undefined = useMemo(() => {
    const {
      name,
      statuses,
      propertyName,
      workflowId,
      optional,
      relations = []
    } = dependency;
    if (isValidStatus && statuses && propertyName && workflowId) {
      return { name, statuses, propertyName, workflowId, relations, optional };
    }
  }, [dependency, isValidStatus]);

  // clear state
  useEffect(() => {
    if (!open) {
      setDependency({ relations: [], statuses: [] });
    }
  }, [open]);

  useEffect(() => {
    dependencyProp && setDependency(dependencyProp);
  }, [dependencyProp]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box>
          <TextField
            fullWidth
            variant='outlined'
            label='Display Name'
            placeholder='Enter the display name of the Dependency'
            value={dependency.name || ''}
            onChange={event => {
              setDependency(dependency => {
                return { ...dependency, name: event.target.value };
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <EntitySchemaSelector
            disabled
            value={schema.name}
            textFieldProps={{ label: 'Workflow Entity' }}
          />
        </Box>
        {properties.map((property, index) => (
          <StyledBox mt={2} key={index}>
            <Grid container spacing={0}>
              <Grid item className='grid-field'>
                <EntitySchemaRelationSelector
                  schema={property.schema}
                  value={property.propertyName}
                  textFieldProps={{
                    label: index === 0 ? 'Dependent Entity' : 'Entity Relation',
                    placeholder: `Select a Relationship from ${
                      property.schema?.displayName || '?'
                    }`
                  }}
                  onChange={relation => {
                    const propertyName = relation?.propertyName;
                    setDependency(dependency => {
                      dependency = { ...dependency };
                      if (!dependency.relations) {
                        dependency.relations = [];
                      }
                      if (index === 0) {
                        dependency.propertyName = propertyName;
                      } else {
                        dependency.relations[index - 1] = propertyName || '';
                      }
                      if (!propertyName) {
                        dependency.statuses = [];
                        dependency.workflowId = undefined;
                        // unset all subsequent relations
                        dependency.relations.splice(index);
                      }
                      return dependency;
                    });
                  }}
                />
              </Grid>
              {index > 0 && (
                <Grid item className='grid-action'>
                  <IconButton
                    onClick={() => {
                      setDependency(dependency => {
                        dependency = {
                          ...dependency,
                          statuses: [],
                          workflowId: undefined
                        };
                        dependency.relations?.splice(index - 1);
                        return dependency;
                      });
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </StyledBox>
        ))}
        <Box mt={2}>
          <Button
            fullWidth
            variant='outlined'
            startIcon={<AddIcon />}
            // disabled if no selected for last property
            disabled={!properties[properties.length - 1].propertyName}
            onClick={() => {
              setDependency(dependency => {
                dependency = {
                  ...dependency,
                  statuses: [],
                  workflowId: undefined
                };
                if (!dependency.relations) {
                  dependency.relations = [];
                }
                dependency.relations.push('');
                return dependency;
              });
            }}
          >
            Add Relation
          </Button>
        </Box>
        <Box mt={2}>
          <WorkflowSelector
            value={dependency.workflowId}
            disabled={!dependency.propertyName}
            relationTarget={relation?.target}
            textFieldProps={{
              label: 'Dependent Entity Workflow',
              placeholder: `Select the Workflow of ${
                relation?.displayName || 'the Dependent Entity'
              }`
            }}
            onChange={value => {
              setDependency(dependency => {
                dependency = { ...dependency, workflowId: value?.id };
                if (!dependency.workflowId) {
                  dependency.statuses = [];
                }
                return dependency;
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <Autocomplete
            multiple
            options={statuses}
            value={dependency.statuses || []}
            disabled={!dependency.workflowId}
            onChange={(_, statuses) => {
              setDependency(dependency => ({ ...dependency, statuses }));
            }}
            renderInput={params => {
              const error =
                !isValidStatus &&
                Array.isArray(dependency.statuses) &&
                dependency.statuses.length > 0;
              return (
                <TextField
                  {...params}
                  fullWidth
                  variant='outlined'
                  label='Transition States'
                  placeholder={`Select the statuses for ${
                    workflow ? workflow.stateNode.name : 'the Workflow'
                  }`}
                  error={error}
                  helperText={
                    error
                      ? 'Invalid state found that is not part of the selected Workflow.'
                      : undefined
                  }
                />
              );
            }}
          />
        </Box>
        <Box mt={2}>
          <FormControlLabel
            label='Optional Dependency'
            control={
              <Checkbox
                color='primary'
                checked={dependency.optional || false}
                onChange={(_, checked) => {
                  setDependency(dependency => {
                    return { ...dependency, optional: checked };
                  });
                }}
              />
            }
          />
          <FormHelperText>
            When checked, <strong>optional dependencies</strong> will allow the
            entity to transition when there are no dependencies to check. (e.g.
            No linked documents will allow transition)
          </FormHelperText>
        </Box>

        <Collapse in={!!(relation && workflow && entityDependency)}>
          <Box mt={2}>
            <Alert severity='info'>
              This condition will transition the entity (
              <strong>{schema.displayName}</strong>) when its dependencies (
              {displayEntityProperties(properties)}) are in the{' '}
              <strong>{workflow?.stateNode.name || '?'}</strong> {'->'}{' '}
              <strong>{displayStatuses(entityDependency?.statuses)}</strong>{' '}
              {(entityDependency?.statuses?.length || 0) > 1
                ? `states`
                : 'state'}
              .
            </Alert>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!entityDependency}
          onClick={() => {
            if (entityDependency) {
              onSubmit?.(entityDependency);
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CheckDependenciesDisplay: React.FC<
  TransitionConditionDisplayProps
> = () => {
  return <>Check Dependencies</>;
};

const StyledTableCell = styled(TableCell)`
  .MuiSvgIcon-root {
    &.success {
      color: ${props => props.theme.palette.success.main};
    }
    &.info {
      color: ${props => props.theme.palette.info.main};
    }
    &.error {
      color: ${props => props.theme.palette.error.main};
    }
    &.warn {
      color: ${props => props.theme.palette.warning.main};
    }
  }
`;

interface CheckDependenciesDialogProps {
  state: State<TaskContext>;
  params: CheckDependenciesParams;
  onCancel?: () => void;
  onConfirm?: () => void;
}

const CheckDependenciesDialog: React.FC<
  CheckDependenciesDialogProps
> = props => {
  const { state, params } = props;
  const { data, isLoading, error, checkDependencies } = useCheckDependencies(
    state.context.task.id || 0
  );

  useEffect(() => {
    checkDependencies(params);
  }, [params, checkDependencies]);

  return (
    <CheckDependenciesDialogView
      {...props}
      error={error}
      isLoading={isLoading}
      success={!!data?.success}
      dependencies={data?.dependencies}
    />
  );
};

export interface CheckDependenciesDialogViewProps {
  params: { dependencies?: { statuses: string[]; [key: string]: any }[] };
  success: boolean;
  isLoading?: boolean;
  error?: FetchError;
  dependencies: CheckDependenciesResult['dependencies'] | undefined;
  onCancel?: () => void;
  onConfirm?: () => void;
}

export const CheckDependenciesDialogView: React.FC<
  CheckDependenciesDialogViewProps
> = props => {
  const {
    params,
    success,
    isLoading,
    error,
    dependencies,
    onCancel,
    onConfirm
  } = props;

  const getLabelAndIcon = (
    result: CheckDependenciesResult['dependencies'][number]
  ) => {
    const [label, color, StatusIcon] =
      result.none && !(result.optional && result.total === 0)
        ? ['None', 'error', HighlightOffIcon]
        : result.partial
        ? ['Partial', 'warn', OfflinePinOutlinedIcon]
        : result.success
        ? ['All', 'success', CheckCircleOutlineIcon]
        : ['None (optional)', 'info', CheckCircleOutlineIcon];
    return { label, icon: <StatusIcon className={color} /> };
  };

  return (
    <Dialog open onClose={onCancel}>
      <DialogTitle>
        {isLoading ? 'Checking Dependencies' : 'Check Dependencies'}
      </DialogTitle>
      <DialogContent style={{ padding: 0 }}>
        {isLoading && (
          <Box
            mx={3}
            my={2}
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            <CircularProgress />
          </Box>
        )}
        {dependencies && !isLoading && (
          <>
            <Box mx={3}>
              <DialogContentText>
                All dependencies need to be in their{' '}
                <strong>Required State/s</strong> state to be able to transition
                this item.
              </DialogContentText>
            </Box>
            <Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Dependency</TableCell>
                      <TableCell>Required State/s</TableCell>
                      <TableCell>Valid Items</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dependencies.map((result, index) => {
                      const dependency = params.dependencies?.[index];
                      const { label, icon } = getLabelAndIcon(result);
                      return (
                        <TableRow key={index}>
                          <WrappedTableCell>
                            {result.name}
                            {result.optional && (
                              <>
                                {' '}
                                <Typography
                                  variant='body2'
                                  color='textSecondary'
                                  style={{ display: 'inline-block' }}
                                >
                                  (optional)
                                </Typography>
                              </>
                            )}
                          </WrappedTableCell>
                          <WrappedTableCell>
                            {displayStatuses(dependency?.statuses, false)}
                          </WrappedTableCell>
                          <WrappedTableCell>
                            <strong>{result.valid}</strong> of{' '}
                            <strong>{result.total}</strong>
                          </WrappedTableCell>
                          <StyledTableCell>
                            <Tooltip arrow title={label}>
                              {icon}
                            </Tooltip>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}

        <Collapse in={success}>
          <Box mt={2} mx={3}>
            <Alert severity='success'>All dependency checks passed!</Alert>
          </Box>
        </Collapse>

        <Collapse in={!isLoading && !!error}>
          <Box mt={2} mx={3}>
            <Alert severity='error'>
              {error?.message || 'An error occurred.'}
            </Alert>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={onCancel}>
          {success ? 'Cancel' : 'Close'}
        </Button>
        {success && (
          <Button
            color='primary'
            variant='contained'
            disabled={isLoading}
            onClick={onConfirm}
          >
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const CheckDependenciesPlugin: TransitionConditionPlugin<CheckDependenciesParams> =
  {
    type: 'check-dependencies',
    label: 'Check Dependencies',
    configurator: CheckDependenciesConfigurator,
    display: CheckDependenciesDisplay,
    validate: (condition: Partial<StateTransitionCondition>) => {
      const params = condition.params as CheckDependenciesParams;
      return (
        !!params?.entityName &&
        Array.isArray(params.dependencies) &&
        params.dependencies.length > 0
      );
    },
    guard: (state: State<TaskContext>, params: WorkflowParams, render) => {
      console.log('guards: check-dependencies', state, params);
      if (
        state.isSimulation ||
        state.context.skipTransitionConditionPlugins?.includes(
          CheckDependenciesPlugin.type
        )
      ) {
        return true;
      }
      return new Promise(resolve => {
        render?.(
          <CheckDependenciesDialog
            state={state}
            params={params as CheckDependenciesParams}
            onCancel={() => {
              render?.(undefined);
              resolve(false);
            }}
            onConfirm={() => {
              render?.(undefined);
              resolve(true);
            }}
          />
        );
      });
    }
  };
